/**
 * AssetEditorBar Component
 * Upload / New folder / Delete folder controls for Reseller Assets editors.
 * Rendered only when the server says the current user may edit.
 */

import React, { useRef, useState } from 'react';
import { uploadAsset, createAssetFolder, deleteAssetFolder } from './assetEditorApi';
import './AssetEditor.css';

const AssetEditorBar = ({ currentFolderId, currentFolderName, isEmpty, onChanged, onFolderDeleted }) => {
  const fileInputRef = useRef(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const run = async (label, action) => {
    setBusy(label);
    setError('');
    try {
      await action();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy('');
    }
  };

  const handleFilesChosen = (e) => {
    const chosen = Array.from(e.target.files || []);
    e.target.value = '';
    if (!chosen.length) return;
    run(`Uploading ${chosen.length} file${chosen.length === 1 ? '' : 's'}...`, async () => {
      for (const file of chosen) {
        await uploadAsset(currentFolderId, file);
      }
      onChanged();
    });
  };

  const handleNewFolder = () => {
    const name = window.prompt('New folder name');
    if (!name || !name.trim()) return;
    run('Creating folder...', async () => {
      await createAssetFolder(currentFolderId, name.trim());
      onChanged();
    });
  };

  const handleDeleteFolder = () => {
    if (!window.confirm(`Delete the empty folder "${currentFolderName}"?`)) return;
    run('Deleting folder...', async () => {
      await deleteAssetFolder(currentFolderId);
      onFolderDeleted();
    });
  };

  return (
    <div className="rf-asset-editor-bar">
      <span className="rf-asset-editor-label">
        <i className="fas fa-pen"></i> Editing Reseller Assets
      </span>
      <div className="rf-asset-editor-actions">
        <button className="rf-btn rf-btn-primary rf-btn-sm" onClick={() => fileInputRef.current.click()} disabled={!!busy}>
          <i className="fas fa-upload"></i> Upload
        </button>
        <button className="rf-btn rf-btn-secondary rf-btn-sm" onClick={handleNewFolder} disabled={!!busy}>
          <i className="fas fa-folder-plus"></i> New folder
        </button>
        {currentFolderId && isEmpty && (
          <button className="rf-btn rf-btn-danger rf-btn-sm" onClick={handleDeleteFolder} disabled={!!busy}>
            <i className="fas fa-trash"></i> Delete this folder
          </button>
        )}
        <input ref={fileInputRef} type="file" multiple hidden onChange={handleFilesChosen} />
      </div>
      {busy && <span className="rf-asset-editor-status"><i className="fas fa-spinner fa-spin"></i> {busy}</span>}
      {error && <span className="rf-asset-editor-error">{error}</span>}
    </div>
  );
};

export default AssetEditorBar;
